import React, { useState, useEffect, useRef, useMemo, createContext, useContext } from 'react';
import SaveAsMealPlanModal from './SaveAsMealPlanModal.js';
import RecipeTooltip from './RecipeTooltip';

const DragDropContext = createContext();

const Recipe = ({ recipe, index, onRecipeRemove, cookbooks, onServingSizeChange, day, onMove, isFirst, isLast, onDragStart, onDragOver, onDrop, onDragLeave, dragOverRecipeId, onMouseEnter, onMouseLeave, onTooltipClick, tooltip, onTooltipClose }) => {
  const getCookbookTitle = (cookbookId) => {
    const cookbook = cookbooks.find(c => c.id === cookbookId);
    return cookbook ? cookbook.title : '';
  };

  const cookbookTitle = getCookbookTitle(recipe.cookbookId);

  const isDragOver = dragOverRecipeId === recipe.id;

  return (
    <>
      <li
        className={`meal-plan-recipe ${isDragOver ? 'drag-over' : ''}`}
        draggable
        onDragStart={(e) => onDragStart(e, recipe.id)}
        onDragOver={(e) => onDragOver(e, recipe.id)}
        onDrop={(e) => { e.stopPropagation(); onDrop(e, recipe.id); }}
        onDragLeave={onDragLeave}
        onMouseEnter={(e) => onMouseEnter(e, recipe)}
        onMouseLeave={onMouseLeave}
        onClick={onTooltipClose}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <div className="move-recipe-buttons">
          <button title="Move recipe up" disabled={isFirst} className="icon-button" onClick={() => onMove(recipe.id, 'up')}><i className="material-icons">keyboard_arrow_up</i></button>
          <button title="Move recipe down" disabled={isLast} className="icon-button" onClick={() => onMove(recipe.id, 'down')}><i className="material-icons">keyboard_arrow_down</i></button>
        </div>
        <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <button
            className="icon-button mobile-view-button"
            onClick={(e) => {
              e.stopPropagation();
              onTooltipClick(e, recipe);
            }}
            title="View details"
            style={{ marginRight: '5px' }}
          >
            <i className="material-icons">visibility</i>
          </button>
          <span>
            {recipe.name}{cookbookTitle && ` - ${cookbookTitle}`}{recipe.pageNumber && ` p.${recipe.pageNumber}`}
          </span>

        </div>
        <div className="meal-plan-recipe-servings-input" style={{ flex: '0 0 100px', marginRight: '10px' }}>
          <input
            type="number"
            value={recipe.servingSize}
            onChange={(e) => onServingSizeChange(recipe.id, e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div className="meal-plan-recipe-servings-print" style={{ flex: '0 0 100px', marginRight: '10px' }}>
          Serves: {recipe.servingSize}
        </div>
        <div className="item-actions">
          <button title="Remove recipe from meal plan" onClick={() => onRecipeRemove(recipe.id)} className="icon-button delete-button"><i className="material-icons">close</i></button>
        </div>
      </li>
    </>
  );
};

const Day = ({ dayNumber, recipes, onMove, orderedRecipes, onDragOver, onDrop, onDragStart, onDragLeave, dragOverRecipeId, dropPosition, startDay, onMouseEnter, onMouseLeave, onTooltipClick, tooltip, onTooltipClose, ...otherProps }) => {
  const { draggedRecipeId } = useContext(DragDropContext);

  const getDayLabel = (dayNum, startDayName) => {
    if (!startDayName) return `Day ${dayNum}`;
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const startIndex = days.indexOf(startDayName);
    if (startIndex === -1) return `Day ${dayNum}`;
    const dayIndex = (startIndex + dayNum - 1) % 7;
    return `Day ${dayNum} - ${days[dayIndex]}`;
  };

  return (
    <div className="day-row" data-tour={`day-slot-${dayNumber}`}>
      <div className="day-label">
        <h4>{getDayLabel(dayNumber, startDay)}</h4>
      </div>
      <div
        className="day-slot"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => onDrop(e, null, dayNumber)}
      >
        <ul className="recipe-list-day">
          {recipes.map((recipe, index) => (
            <React.Fragment key={recipe.id}>
              {draggedRecipeId !== recipe.id && dragOverRecipeId === recipe.id && dropPosition === 'top' && <li className="recipe-drop-placeholder" onDragOver={(e) => onDragOver(e, recipe.id)} onDrop={(e) => { e.stopPropagation(); onDrop(e, recipe.id); }} />}
              <Recipe
                index={index}
                recipe={recipe}
                recipes={recipes}
                day={dayNumber}
                {...otherProps}
                onMove={onMove}
                isFirst={orderedRecipes.findIndex(r => r.id === recipe.id) === 0 && dayNumber === 1}
                isLast={false}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragStart={onDragStart}
                onDragLeave={onDragLeave}
                dragOverRecipeId={dragOverRecipeId}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onTooltipClick={onTooltipClick}
                tooltip={tooltip}
                onTooltipClose={onTooltipClose}
              />
              {draggedRecipeId !== recipe.id && dragOverRecipeId === recipe.id && dropPosition === 'bottom' && <li className="recipe-drop-placeholder" onDragOver={(e) => onDragOver(e, recipe.id)} onDrop={(e) => { e.stopPropagation(); onDrop(e, recipe.id); }} />}
            </React.Fragment>
          ))}
          {recipes.length === 0 && draggedRecipeId && (
            <li className="recipe-drop-placeholder" />
          )}
          {recipes.length === 0 && !draggedRecipeId && (
            <div className="empty-day-placeholder">Drop a recipe here</div>
          )}
        </ul>
      </div>
    </div>
  );
};

const AnyDaySection = ({ recipes, onMove, orderedRecipes, onDragStart, onDragOver, onDrop, onDragLeave, onRecipeRemove, cookbooks, onServingSizeChange, dragOverRecipeId, dropPosition, onMouseEnter, onMouseLeave, onTooltipClick, tooltip, onTooltipClose, numberOfDays }) => {
  const { draggedRecipeId } = useContext(DragDropContext);

  return (
    <div className="day-row">
      <div className="day-label">
        <h4>Any day</h4>
      </div>
      <div
        className="day-slot"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => onDrop(e, null, null)}
      >
        <ul className="recipe-list-day">
          {recipes.map((recipe, index) => (
            <React.Fragment key={recipe.id}>
              {draggedRecipeId !== recipe.id && dragOverRecipeId === recipe.id && dropPosition === 'top' && <li className="recipe-drop-placeholder" onDragOver={(e) => onDragOver(e, recipe.id)} onDrop={(e) => { e.stopPropagation(); onDrop(e, recipe.id); }} />}
              <Recipe
                index={index}
                recipe={recipe}
                recipes={recipes}
                onRecipeRemove={onRecipeRemove}
                cookbooks={cookbooks}
                onServingSizeChange={onServingSizeChange}
                day={null}
                onMove={onMove}
                isFirst={orderedRecipes.findIndex(r => r.id === recipe.id) === 0 && numberOfDays <= 0}
                isLast={orderedRecipes.findIndex(r => r.id === recipe.id) === orderedRecipes.length - 1}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragLeave={onDragLeave}
                dragOverRecipeId={dragOverRecipeId}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onTooltipClick={onTooltipClick}
                tooltip={tooltip}
                onTooltipClose={onTooltipClose}
              />
              {draggedRecipeId !== recipe.id && dragOverRecipeId === recipe.id && dropPosition === 'bottom' && <li className="recipe-drop-placeholder" onDragOver={(e) => onDragOver(e, recipe.id)} onDrop={(e) => { e.stopPropagation(); onDrop(e, recipe.id); }} />}
            </React.Fragment>
          ))}
          {recipes.length === 0 && draggedRecipeId && (
            <li className="recipe-drop-placeholder" />
          )}
          {recipes.length === 0 && !draggedRecipeId && (
            <div className="empty-day-placeholder">Drop a recipe here</div>
          )}
        </ul>
      </div>
    </div>
  );
};

const MealPlan = ({ recipes, mealPlan, onRecipeRemove, onRecipeReorder, onSave, cookbooks, isMealPlanDirty, onEdit, onServingSizeUpdate, globalServingSize, onAddAdditionalItem, onDeleteAdditionalItem, onUpdateAdditionalItem, onCloseMealPlan, onNumberOfDaysChange, onMove, startTour, setIsFaqModalOpen, onStartDayChange }) => {
  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);
  const [showSaveFeedback, setShowSaveFeedback] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(true);
  const footerRef = useRef(null);
  const [newItemName, setNewItemName] = useState('');
  const [newItemAmount, setNewItemAmount] = useState('');
  const [newItemUnit, setNewItemUnit] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemData, setEditingItemData] = useState({ name: '', amount: '', unit: '' });
  const [numberOfDays, setNumberOfDays] = useState(mealPlan.numberOfDays ?? 3);
  const newItemNameInputRef = useRef(null);
  const dragLeaveTimeoutRef = useRef(null);
  const editingItemNameInputRef = useRef(null);
  const [draggedRecipeId, setDraggedRecipeId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [dragOverRecipeId, setDragOverRecipeId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, recipe: null, target: null, triggeredBy: null });
  const tooltipTimer = useRef(null);

  const recipeMap = useMemo(() =>
    recipes.reduce((acc, recipe) => {
      acc[recipe.id] = recipe;
      return acc;
    }, {}),
    [recipes]);

  const enrichedMealPlanRecipes = useMemo(() =>
    mealPlan.recipes.map(mealPlanRecipe => {
      // Lookup using recipeId (new structure) or id (legacy structure)
      const fullRecipe = recipeMap[mealPlanRecipe.recipeId || mealPlanRecipe.id];
      return fullRecipe ? { ...fullRecipe, ...mealPlanRecipe, id: mealPlanRecipe.id } : null;
    }).filter(Boolean),
    [mealPlan.recipes, recipeMap]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isMealPlanDirty) {
        setIsFooterVisible(false);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [isMealPlanDirty]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (footerRef.current) {
        const footerTop = footerRef.current.getBoundingClientRect().top;
        if (event.clientY > footerTop - 100) {
          setIsFooterVisible(true);
        } else if (!isMealPlanDirty) {
          setIsFooterVisible(false);
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isMealPlanDirty]);

  const orderedRecipes = useMemo(() => {
    const byDay = [];
    for (let i = 1; i <= (mealPlan.numberOfDays ?? 3); i++) {
      byDay.push(...enrichedMealPlanRecipes.filter(r => r.day === i).sort((a, b) => a.order - b.order));
    }
    const unassigned = enrichedMealPlanRecipes.filter(r => !r.day).sort((a, b) => a.order - b.order);
    return [...byDay, ...unassigned];
  }, [enrichedMealPlanRecipes, mealPlan.numberOfDays]);

  useEffect(() => {
    if (editingItemId !== null && editingItemNameInputRef.current) {
      editingItemNameInputRef.current.focus();
    }
  }, [editingItemId]);

  useEffect(() => {
    setNumberOfDays(mealPlan.numberOfDays ?? 3);
  }, [mealPlan.numberOfDays]);

  const handleGlobalServingSizeChange = (e) => {
    const newSize = e.target.value;
    onServingSizeUpdate(null, newSize, true);
  };

  const handleServingSizeChange = (recipeId, newSize) => {
    onServingSizeUpdate(recipeId, newSize, false);
  };

  const handleAddClick = () => {
    if (newItemName) {
      onAddAdditionalItem({ name: newItemName, amount: newItemAmount, unit: newItemUnit }, 'manual');
      setNewItemName('');
      setNewItemAmount('');
      setNewItemUnit('');
      if (newItemNameInputRef.current) {
        newItemNameInputRef.current.focus();
      }
    }
  };

  const handleEditClick = (item) => {
    setEditingItemId(item.id);
    setEditingItemData(item);
  };

  const handleUpdateClick = () => {
    onUpdateAdditionalItem(editingItemData);
    setEditingItemId(null);
    setEditingItemData({ name: '', amount: '', unit: '' });
  };

  const handleCancelClick = () => {
    setEditingItemId(null);
    setEditingItemData({ name: '', amount: '', unit: '' });
  };

  const handleEditingItemChange = (field, value) => {
    setEditingItemData(prevData => ({ ...prevData, [field]: value }));
  };

  const handleUpdateItemKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleUpdateClick();
    }
  };

  const handleAddItemKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddClick();
    }
  };

  const handleNumberOfDaysChange = (e) => {
    let newDays = parseInt(e.target.value, 10);
    if (isNaN(newDays) || newDays < 0) {
      newDays = 0;
    }
    onNumberOfDaysChange(newDays);
  };

  const handlePrintMealPlan = () => {
    document.body.classList.add('printing-meal-plan');
    document.body.classList.remove('printing-ingredients');
    window.print();
    document.body.classList.remove('printing-meal-plan');
  };

  const closeTooltip = () => {
    setTooltip({ visible: false, recipe: null, target: null, triggeredBy: null });
  };

  const handleDragStart = (e, recipeId) => {
    closeTooltip();
    setDraggedRecipeId(recipeId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, recipeId) => {
    e.preventDefault();
    if (dragLeaveTimeoutRef.current) {
      clearTimeout(dragLeaveTimeoutRef.current);
    }

    if (e.currentTarget.classList.contains('recipe-drop-placeholder')) {
      // Keep the current drop indicator active when hovering over the placeholder itself
      return;
    }

    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const position = e.clientY < midY ? 'top' : 'bottom';

    if (draggedRecipeId !== recipeId) {
      setDragOverRecipeId(recipeId);
      setDropPosition(position);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragLeaveTimeoutRef.current = setTimeout(() => {
      setDragOverRecipeId(null);
      setDropPosition(null);
    }, 50);
  };

  const handleDrop = (e, targetRecipeId, targetDay) => {
    e.preventDefault();
    if (dragLeaveTimeoutRef.current) {
      clearTimeout(dragLeaveTimeoutRef.current);
    }
    onRecipeReorder(draggedRecipeId, targetRecipeId, targetDay, dropPosition);
    setDraggedRecipeId(null);
    setDropTargetId(null);
    setDragOverRecipeId(null);
    setDropPosition(null);
  };

  // Tooltip handlers
  const handleMouseEnter = (e, recipe) => {
    if (window.innerWidth < 768) return;
    const target = e.currentTarget;
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
    }
    tooltipTimer.current = setTimeout(() => {
      setTooltip({
        visible: true,
        recipe,
        target,
        triggeredBy: 'hover'
      });
    }, 500); // 500ms delay
  };

  const handleMouseLeave = () => {
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
    }
    if (tooltip.triggeredBy === 'hover') {
      setTooltip({ visible: false, recipe: null, target: null, triggeredBy: null });
    }
  };

  const handleTooltipClick = (e, recipe) => {
    if (tooltip.visible && tooltip.recipe && tooltip.recipe.id === recipe.id && tooltip.triggeredBy === 'click') {
      setTooltip({ visible: false, recipe: null, target: null, triggeredBy: null });
    } else {
      setTooltip({ visible: true, recipe, target: e.currentTarget, triggeredBy: 'click' });
    }
  };

  // AnyDaySection removed from here


  return (
    <div className="meal-plan-container section meal-plan-print-section" data-tour="meal-plan">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="meal-plan-title-container">
          <i className="material-icons" style={{ marginRight: '5px' }}>event_note</i>
          <h2>Meal plan {mealPlan.title && `- ${mealPlan.title}`}</h2>
          <div className="item-actions">
            {mealPlan.id && (
              <button onClick={() => onEdit(mealPlan)} className="icon-button edit-meal-plan-title-button" title="Edit meal plan title">
                <i className="material-icons">edit</i>
              </button>
            )}
          </div>
        </div>
        {(mealPlan.id || mealPlan.recipes.length > 0 || (mealPlan.additionalItems && mealPlan.additionalItems.length > 0)) && (
          <button onClick={onCloseMealPlan} className="icon-button" title="Close meal plan"><i className="material-icons">close</i></button>
        )}
      </div>
      {(mealPlan.recipes.length > 0) && (
        <div className="days-servings-container" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ flex: '0 0 100px', marginRight: '10px' }} data-tour="meal-plan-days">
            <label># of days</label>
            <input
              type="number"
              value={numberOfDays}
              onChange={handleNumberOfDaysChange}
              style={{ width: '100%' }}
              min="0"
            />
          </div>
          <div style={{ flex: '0 0 150px', marginRight: '10px' }}>
            <label>Start Day</label>
            <select
              value={mealPlan.startDay || ''}
              onChange={(e) => onStartDayChange(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value=""></option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Saturday">Saturday</option>
              <option value="Sunday">Sunday</option>
            </select>
          </div>
          <div style={{ flexGrow: 1 }}></div>
          <div style={{ flex: '0 0 100px', marginRight: '10px' }} data-tour="meal-plan-servings">
            <label>Servings</label>
            <input
              type="number"
              value={globalServingSize}
              onChange={handleGlobalServingSizeChange}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '0 0 50px' }}></div>
        </div>
      )}

      <DragDropContext.Provider value={{ draggedRecipeId }}>
        {(mealPlan.recipes.length > 0) && Array.from({ length: numberOfDays }, (_, i) => i + 1).map(day => (
          <Day
            key={day}
            dayNumber={day}
            recipes={enrichedMealPlanRecipes.filter(r => r.day === day).sort((a, b) => a.order - b.order)}
            onRecipeRemove={onRecipeRemove}
            cookbooks={cookbooks}
            onServingSizeChange={handleServingSizeChange}
            onMove={onMove}
            orderedRecipes={orderedRecipes}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragStart={handleDragStart}
            onDragLeave={handleDragLeave}
            dragOverRecipeId={dragOverRecipeId}
            dropPosition={dropPosition}
            startDay={mealPlan.startDay}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTooltipClick={handleTooltipClick}
            tooltip={tooltip}
            onTooltipClose={closeTooltip}
          />
        ))}
        {(mealPlan.recipes.length > 0) && (
          <AnyDaySection
            recipes={enrichedMealPlanRecipes.filter(r => !r.day).sort((a, b) => a.order - b.order)}
            onMove={onMove}
            orderedRecipes={orderedRecipes}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
            onRecipeRemove={onRecipeRemove}
            cookbooks={cookbooks}
            onServingSizeChange={handleServingSizeChange}
            dragOverRecipeId={dragOverRecipeId}
            dropPosition={dropPosition}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTooltipClick={handleTooltipClick}
            tooltip={tooltip}
            onTooltipClose={closeTooltip}
            numberOfDays={numberOfDays}
          />
        )}
      </DragDropContext.Provider>

      {mealPlan.recipes.length === 0 && (!mealPlan.additionalItems || mealPlan.additionalItems.length === 0) && <p>Create a plan by selecting 1 or more recipes or item groups.<br />You can also start by adding additional items below.</p>}
      <div className="additional-items" style={{ alignItems: 'center' }}>
        <input ref={newItemNameInputRef} placeholder="Additional item..." type="text" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} onKeyDown={handleAddItemKeyDown} />
        <input placeholder="Amount" type="number" value={newItemAmount} onChange={(e) => setNewItemAmount(e.target.value)} onKeyDown={handleAddItemKeyDown} />
        <select value={newItemUnit} onChange={(e) => setNewItemUnit(e.target.value)} onKeyDown={handleAddItemKeyDown}><option value=""></option><option value="tsp">tsp</option><option value="tbsp">tbsp</option><option value="cup">cup</option><option value="oz">oz</option><option value="lb">lb</option><option value="g">g</option><option value="kg">kg</option><option value="ml">ml</option></select>
        <button className="plus-button" title="Add item" onClick={handleAddClick}><i className="material-icons">add</i></button>
      </div>
      <ul>
        {(mealPlan.additionalItems || []).slice().sort((a, b) => a.name.localeCompare(b.name)).map((item, index) => (
          <li className="additional-item" key={item.id} style={{ display: 'flex', alignItems: 'center' }}>
            {editingItemId === item.id ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                <input ref={editingItemNameInputRef} id="additional-item-name" type="text" value={editingItemData.name} onChange={(e) => handleEditingItemChange('name', e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') { handleCancelClick(); } else { handleUpdateItemKeyDown(e); } }} />
                <input type="number" value={editingItemData.amount} onChange={(e) => handleEditingItemChange('amount', e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') { handleCancelClick(); } else { handleUpdateItemKeyDown(e); } }} />
                <select value={editingItemData.unit} onChange={(e) => handleEditingItemChange('unit', e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') { handleCancelClick(); } else { handleUpdateItemKeyDown(e); } }}>
                  <option value=""></option>
                  <option value="tsp">tsp</option>
                  <option value="tbsp">tbsp</option>
                  <option value="cup">cup</option>
                  <option value="oz">oz</option>
                  <option value="lb">lb</option>
                  <option value="g">g</option>
                  <option value="kg">kg</option>
                  <option value="ml">ml</option>
                </select>
                <button title="Update item" onClick={handleUpdateClick} className="icon-button save-button"><i className="material-icons">check</i></button>
                <button title="Cancel update" onClick={handleCancelClick} className="icon-button delete-button"><i className="material-icons">close</i></button>
              </div>
            ) : (
              <>
                <div style={{ flexGrow: 1 }}>
                  {item.name} {item.amount} {item.unit}
                </div>
                <div className="item-actions">
                  <button onClick={() => handleEditClick(item)} className="icon-button" title="Edit item"><i className="material-icons">edit</i></button>
                  <button onClick={() => onDeleteAdditionalItem(item.id)} className="icon-button delete-button"><i className="material-icons">close</i></button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
      {(mealPlan.recipes.length > 0 || (mealPlan.additionalItems && mealPlan.additionalItems.length > 0)) && (
        <div>
          <p className="print-hide"><br /><em>* If you are planning a vacation, a work trip, or some other event far in advance, you can save this meal plan and simply reload it later in case changes come up. This is useful as plans are often fluid in the early stages.</em></p>
          <div className="save-buttons-container" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }} data-tour="meal-plan-save-button">
            {!mealPlan.id ? (
              <></>
            ) : (
              <>
                <button title="Copy meal plan" onClick={() => setIsSaveAsModalOpen(true)} style={{ marginRight: '10px' }}>Copy</button>
              </>
            )}
            <button title="Print meal plan" onClick={handlePrintMealPlan} className="print-button" style={{ marginLeft: '10px' }}>
              <i className="material-icons">print</i> Print
            </button>
          </div>
        </div>
      )}
      <div ref={footerRef} className={`footer-toolbar ${isFooterVisible || isMealPlanDirty ? 'visible' : ''}`}>
        <div className="tour-buttons-footer hide-on-mobile">
          <button title="How do I...?" onClick={() => { setIsFaqModalOpen(true); }} className="how-do-i-button">
            <i className="material-icons">help_outline</i>How do I?
          </button>
          <button title="Start a tour" onClick={startTour} className="tour-start-button">
            <i className="material-icons">auto_awesome</i>Take a Tour
          </button>
        </div>
        <div style={{ flexGrow: 1 }}></div>
        {(isMealPlanDirty || mealPlan.id) && (
          <div className="save-buttons-container" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }} data-tour="meal-plan-save-button">
            {isSaving && <i className="material-icons spin" style={{ marginRight: '10px' }}>autorenew</i>}
            {showSaveFeedback && <span style={{ marginRight: '10px', color: 'green' }}>Saved!</span>}
            {!mealPlan.id ? (
              <button style={{ fontSize: '16px', marginRight: '0px' }} title="Save meal plan" onClick={() => setIsSaveAsModalOpen(true)}>Save</button>
            ) : (
              <button style={{ fontSize: '16px', marginRight: '0px' }}
                title="Save meal plan"
                onClick={() => {
                  setIsSaving(true);
                  onSave(mealPlan.title, false, {
                    onSuccess: () => {
                      setIsSaving(false);
                      setShowSaveFeedback(true);
                      setTimeout(() => setShowSaveFeedback(false), 2000);
                    },
                    onError: () => {
                      setIsSaving(false);
                    }
                  });
                }}
                disabled={!isMealPlanDirty || isSaving}
              >Save</button>
            )}
          </div>
        )}
      </div>
      {tooltip.visible && (
        <RecipeTooltip
          recipe={tooltip.recipe}
          target={tooltip.target}
          cookbooks={cookbooks}
          mobileMode={tooltip.triggeredBy === 'click'}
          onClose={() => setTooltip({ visible: false, recipe: null, target: null, triggeredBy: null })}
        />
      )}
      <SaveAsMealPlanModal
        isOpen={isSaveAsModalOpen}
        onClose={() => setIsSaveAsModalOpen(false)}
        onSave={(name) => {
          onSave(name, true, {
            onSuccess: () => {
              setIsSaveAsModalOpen(false);
              setShowSaveFeedback(true);
              setTimeout(() => setShowSaveFeedback(false), 2000);
            }
          });
        }}
      />
    </div>
  );
};

export default MealPlan;